import * as React from 'react';
import * as StringUtils from '@packages/utilities/string-utils';
import * as FileUploadUtils from '@packages/shared/utils/FileUploadUtils';
import * as BackgroundVideoHelpersShared from './BackgroundVideoHelpersShared';
import {ScheduledTranscode} from './UploadVideoUtils';
import {
  BACKGROUND_VIDEO_CANCELLED,
  BACKGROUND_VIDEO_CHANGED,
  BACKGROUND_VIDEO_TEMP_ID_CHANGED,
  BACKGROUND_VIDEO_UPLOAD_FAILED,
} from '@packages/systems/designer/actions';
import {
  Expressions,
  withSystem,
  Plugins,
  Component,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  qualify,
  getName,
  typeName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';
import {
  selectCurrentComponentType,
  selectSelectedNodeNativeId,
} from '@packages/utilities/platform-selectors';
import {DEFAULT_PLAY_PAUSE_BUTTON} from '../BackgroundVideoPlayPauseButton';
import {
  type Expression,
  TText,
  TRecord,
  TList,
  TListOfElement,
  TBoolean,
  EBoolean,
  EList,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {findChild} from '@packages/utilities/platform';
import {isPlayPauseButton} from '@packages/utilities/elementTypeUtils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  NewTabIcon,
  VideoPlayIcon,
  StyledBackgroundVideoInfoText,
  StyledCenteredSettingsText,
  StyledSettingsText,
  StyledSettingsFileText,
  StyledSettingsProgress,
  StyledSettingsProgressText,
  StyledVideoProgressBar,
  StyledVideoProgressBarFill,
  StyledVideoProgressBarFillBlue,
  StyledPreviewVideoWrapper,
  StyledVideo,
  StyledUploadInput,
  StyledPlayMask,
} from '../components';
import {
  updateExpressionAction,
  updateExpressionAtAction,
  fieldWrapperLabel,
} from '@packages/systems/ExpressionEditor-utils';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerStores,
  type DesignerFlux,
} from '@packages/systems/designer/flux';
import {
  Button,
  Checkbox,
  RefreshIcon,
  UploadIcon,
  VStack,
} from '@packages/systems/spring';
import {selectIsOnSecondaryLocale} from '@packages/systems/localization';
import {buildBackgroundVideoChangedLocalizationKeys} from '@packages/domains/localization/dom/flux';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';

const fields = {
  tag: withSystem(
    {
      label: 'Tag',
      private: true,
      defaultValue: 'div',
    },
    TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
  ),
  children: TListOfElement,
  bgvideo: withSystem(
    {private: true},
    TRecord({
      type: withSystem({defaultValue: 'wrapper'}, TText),
      original_filename: withSystem({private: true}, TText),
      video_urls: withSystem(
        {private: true, isLocalizable: true},
        TList(TText)
      ),
      poster_image_url: withSystem({private: true, isLocalizable: true}, TText),
    })
  ),
  attr: TRecord({
    'data-poster-url': withSystem({private: true}, TText),
    'data-video-urls': withSystem({private: true}, TText),
    'data-autoplay': withSystem(
      {defaultValue: true, isLocalizable: true},
      TBoolean
    ),
    'data-loop': withSystem(
      {defaultValue: true, isLocalizable: true},
      TBoolean
    ),
    'data-wf-ignore': withSystem({private: true, defaultValue: true}, TBoolean),
  }),
} as const;

const VIDEO_URL_PATH = [
  {in: 'Record', at: 'bgvideo'},
  {in: 'Record', at: 'video_urls'},
] satisfies ExpressionPath;
const VIDEO_TEMP_ID_PATH = [
  ...TEMP_PATH,
  {in: 'Record', at: 'id'},
] satisfies ExpressionPath;
const VIDEO_LOOP_ATTR_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'data-loop'},
] satisfies ExpressionPath;
const VIDEO_AUTOPLAY_ATTR_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'data-autoplay'},
] satisfies ExpressionPath;
const VIDEO_CHILDREN_PATH = [
  {in: 'Record', at: 'children'},
] satisfies ExpressionPath;

const parseVideoUrls = (expr: Expression) => {
  const videoListExpr = Expressions.getIn(expr, VIDEO_URL_PATH);
  const videoList = videoListExpr
    ? Expressions.getList(videoListExpr) || []
    : [];
  return videoList.reduce<Array<any>>((acc, videoExpr) => {
    const text = Expressions.getText(videoExpr);
    if (text) acc.push(text);
    return acc;
  }, []);
};

const parseVideoTempId = (expr: Expression) => {
  const videoTempIdExpr = Expressions.getIn(expr, VIDEO_TEMP_ID_PATH);
  if (!videoTempIdExpr) return null;
  return Expressions.getText(videoTempIdExpr);
};

const arrayEquals = (a: Array<any | string>, b: Array<any | string>) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((val, index) => val === b[index]);

const BackgroundVideoUploadButton = ({
  // @ts-expect-error - TS7031 - Binding element 'hasVideo' implicitly has an 'any' type.
  hasVideo,
  // @ts-expect-error - TS7031 - Binding element 'isUploadingOrTranscoding' implicitly has an 'any' type.
  isUploadingOrTranscoding,
  // @ts-expect-error - TS7031 - Binding element 'isVideoReplaceable' implicitly has an 'any' type.
  isVideoReplaceable,
  // @ts-expect-error - TS7031 - Binding element 'videoRecord' implicitly has an 'any' type.
  videoRecord,
  // @ts-expect-error - TS7031 - Binding element 'setVideoUrls' implicitly has an 'any' type.
  setVideoUrls,
}) => {
  const getInitialCancelUploadVideo = () => {
    if (!videoRecord) return null;
    const fileId = videoRecord._id;
    return BackgroundVideoHelpersShared.getCachedUploadCancel(fileId);
  };

  const [onCancelClick, setOnCancelClick] = React.useState(
    () => getInitialCancelUploadVideo
  );
  const dispatch = useDispatch();
  const componentId = getName(useDesignerSelector(selectCurrentComponentType));
  const nativeId = useDesignerSelector(selectSelectedNodeNativeId);
  const flux = useFluxContext<DesignerFlux>();

  if (!nativeId) return;

  const onChange = (e: any) => {
    const options = {
      // @ts-expect-error - TS7006 - Parameter 'file' implicitly has an 'any' type.
      onStart: (file) => {
        const tempId = file.get('_id');
        dispatch({
          type: BACKGROUND_VIDEO_TEMP_ID_CHANGED,
          payload: {
            componentId,
            nativeId,
            tempId,
          },
        });
      },
      onTranscoded: (data: ScheduledTranscode) => {
        const posterImageUrl = data.poster_image_url;
        const videoUrls = data.video_urls;
        setVideoUrls(videoUrls);

        const localization = buildBackgroundVideoChangedLocalizationKeys(
          flux.state,
          nativeId
        );

        dispatch({
          type: BACKGROUND_VIDEO_CHANGED,
          payload: {
            componentId,
            nativeId,
            video_urls: videoUrls,
            poster_image_url: posterImageUrl,
            localization,
          },
        });
      },
      // @ts-expect-error - TS7006 - Parameter 'err' implicitly has an 'any' type.
      onFail: (err) => {
        console.warn(err);
        dispatch({
          type: BACKGROUND_VIDEO_UPLOAD_FAILED,
        });
      },
      onCancel: () => {
        dispatch({
          type: BACKGROUND_VIDEO_CANCELLED,
          payload: {
            componentId,
            nativeId,
          },
        });
      },
    } as const;

    const cancelUpload = BackgroundVideoHelpersShared.uploadVideo(
      e,
      options,
      flux
    );
    setOnCancelClick(() => cancelUpload);
  };

  return (
    <form method="post" encType="multipart/form-data">
      <input type="hidden" name="key" />
      <input type="hidden" name="AWSAccessKeyId" />
      <input type="hidden" name="acl" value="public-read" />
      <input type="hidden" name="policy" />
      <input type="hidden" name="signature" />
      <input type="hidden" name="success_action_status" value="201" />
      <input type="hidden" name="Content-Type" />

      <Hitarea>
        {isUploadingOrTranscoding ? (
          <Button isFullWidth onClick={onCancelClick}>
            Cancel
          </Button>
        ) : (
          <Button
            isFullWidth
            prefix={
              isVideoReplaceable ? (
                <RefreshIcon />
              ) : !hasVideo ? (
                <UploadIcon />
              ) : null
            }
          >
            <>
              <StyledUploadInput
                data-automation-id="BackgroundVideoUploadInput"
                type="file"
                name="file"
                accept="video/webm,video/mpeg,video/mp4,video/quicktime,video/ogg"
                onChange={onChange}
              />
              {hasVideo ? 'Replace video' : 'Upload video'}
            </>
          </Button>
        )}
      </Hitarea>
    </form>
  );
};

const BackgroundVideoInfoText = ({
  // @ts-expect-error - TS7031 - Binding element 'videoRecord' implicitly has an 'any' type.
  videoRecord,
  // @ts-expect-error - TS7031 - Binding element 'hasVideo' implicitly has an 'any' type.
  hasVideo,
  // @ts-expect-error - TS7031 - Binding element 'isVideoReplaceable' implicitly has an 'any' type.
  isVideoReplaceable,
}) => {
  const {
    origFileName: originalFileName = '',
    s3Url: originalFileUrl = '',
    fileSize: originalFileSize = 0,
  } = videoRecord || {};

  return (
    <StyledBackgroundVideoInfoText>
      {!hasVideo ? (
        <>
          <StyledCenteredSettingsText>
            Supported formats: webm, mp4, mov, ogg
          </StyledCenteredSettingsText>
          <StyledCenteredSettingsText>
            File must be smaller than 30MB
          </StyledCenteredSettingsText>
        </>
      ) : null}
      {isVideoReplaceable ? (
        <>
          <StyledSettingsFileText>{originalFileName}</StyledSettingsFileText>
          <a href={originalFileUrl} target="_blank" rel="noopener noreferrer">
            <NewTabIcon />
          </a>
          <StyledSettingsText>
            Size: {StringUtils.prettySize(originalFileSize)}
          </StyledSettingsText>
        </>
      ) : null}
    </StyledBackgroundVideoInfoText>
  );
};

const BackgroundVideoProgressBar = ({
  // @ts-expect-error - TS7031 - Binding element 'isUploadingOrTranscoding' implicitly has an 'any' type.
  isUploadingOrTranscoding,
  // @ts-expect-error - TS7031 - Binding element 'isUploading' implicitly has an 'any' type.
  isUploading,
  // @ts-expect-error - TS7031 - Binding element 'isTranscoding' implicitly has an 'any' type.
  isTranscoding,
  // @ts-expect-error - TS7031 - Binding element 'uploadProgress' implicitly has an 'any' type.
  uploadProgress,
  // @ts-expect-error - TS7031 - Binding element 'transcodeProgress' implicitly has an 'any' type.
  transcodeProgress,
}) => {
  return isUploadingOrTranscoding ? (
    <StyledSettingsProgress>
      <StyledSettingsProgressText>
        {isUploading ? 'Uploading...' : 'Transcoding...'}
      </StyledSettingsProgressText>
      <StyledVideoProgressBar>
        {isUploading && uploadProgress ? (
          <StyledVideoProgressBarFill
            style={{width: uploadProgress * 100 + '%'}}
          />
        ) : null}
        {isTranscoding && transcodeProgress ? (
          <StyledVideoProgressBarFillBlue
            style={{width: transcodeProgress * 100 + '%'}}
          />
        ) : null}
      </StyledVideoProgressBar>
      <StyledCenteredSettingsText>
        (PS: You can keep designing!)
      </StyledCenteredSettingsText>
    </StyledSettingsProgress>
  ) : null;
};

const selectAssetStore = (state: DesignerStores) => state.AssetStore;
const getIsOnSecondaryLocale = (state: DesignerStores) =>
  selectIsOnSecondaryLocale(state.LocalizationStore);

const BackgroundVideoWrapperTypeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => any;
}) => {
  const videoElement = React.useRef<HTMLVideoElement | null>(null);

  const designerAssetState = useDesignerSelector(selectAssetStore);
  const isOnSecondaryLocale = useDesignerSelector(getIsOnSecondaryLocale);
  const strippedExpr = stripAll(expr);
  const [videoUrls, setVideoUrls] = React.useState(
    parseVideoUrls(strippedExpr)
  );
  const [tempId, setTempId] = React.useState(parseVideoTempId(strippedExpr));

  React.useEffect(() => {
    const newVideoUrls = parseVideoUrls(strippedExpr);
    if (!arrayEquals(videoUrls, newVideoUrls)) setVideoUrls(newVideoUrls);
  }, [strippedExpr, videoUrls]);
  React.useEffect(() => {
    const newTempId = parseVideoTempId(strippedExpr);
    if (tempId !== newTempId) setTempId(newTempId);
  }, [strippedExpr, tempId]);

  const getVideoRecord = () => {
    if (tempId) return designerAssetState.getById(tempId) || null;
    if (videoUrls.length)
      return (
        videoUrls
          .map((url) => {
            const uploadData = FileUploadUtils.getUploadData(url);
            const {guid} = uploadData;
            return designerAssetState.getById(guid) || null;
          })
          .find((asset) => asset !== null) || null
      );
    return null;
  };

  const getHasVideo = () => {
    return Boolean(videoUrls.length);
  };

  const getUploadProgress = () => {
    const videoRecord = getVideoRecord();
    return videoRecord && videoRecord.uploadProgress;
  };

  const getIsUploading = () => {
    return getUploadProgress() !== null;
  };

  const getTranscodeProgress = () => {
    const videoRecord = getVideoRecord();
    return videoRecord && videoRecord.transcodeProgress;
  };

  const getIsTranscoding = () => {
    return getTranscodeProgress() !== null;
  };

  const getIsUploadingOrTranscoding = () => {
    return getIsUploading() || getIsTranscoding();
  };

  const getIsVideoReplaceable = () => {
    return getHasVideo() && !getIsUploadingOrTranscoding();
  };

  const getPreviewVideo = () => {
    return videoUrls.length ? videoUrls[0] : null;
  };

  const onPlayMaskMouseEnter = () => {
    if (!videoElement || !videoElement.current) return;
    videoElement.current.play();
  };

  const onPlayMaskMouseLeave = () => {
    if (!videoElement || !videoElement.current) return;
    videoElement.current.pause();
  };

  const videoRecord = getVideoRecord();
  const hasVideo = getHasVideo();
  const uploadProgress = getUploadProgress();
  const transcodeProgress = getTranscodeProgress();
  const isTranscoding = getIsTranscoding();
  const isUploading = getIsUploading();
  const isUploadingOrTranscoding = getIsUploadingOrTranscoding();
  const isVideoReplaceable = getIsVideoReplaceable();
  const previewVideo = getPreviewVideo();

  const loop = Expressions.getIn(strippedExpr, VIDEO_LOOP_ATTR_PATH);
  const autoplay = Expressions.getIn(strippedExpr, VIDEO_AUTOPLAY_ATTR_PATH);
  const expressionElement = {
    id: 'Background Video Wrapper',
    type: qualify(
      Plugins.name('BackgroundVideo'),
      Component.name('BackgroundVideoWrapper')
    ),
    data: strippedExpr,
  };
  const hasPlayPauseButton = Boolean(
    findChild(expressionElement, isPlayPauseButton)
  );

  const videoKey = React.useMemo(() => videoUrls.join(','), [videoUrls]);

  return (
    <>
      <BackgroundVideoUploadButton
        hasVideo={hasVideo}
        videoRecord={videoRecord}
        isUploadingOrTranscoding={isUploadingOrTranscoding}
        isVideoReplaceable={isVideoReplaceable}
        setVideoUrls={setVideoUrls}
      />
      {hasVideo || isUploadingOrTranscoding ? (
        <StyledPreviewVideoWrapper>
          {isVideoReplaceable && previewVideo ? (
            <StyledVideo key={videoKey} loop ref={videoElement}>
              {/* @ts-expect-error - TS7006 - Parameter 'src' implicitly has an 'any' type. */}
              {previewVideo.split(',').map((src) => (
                <source src={src} key={src} />
              ))}
            </StyledVideo>
          ) : null}
          {isVideoReplaceable ? (
            <Hitarea>
              <StyledPlayMask
                onMouseEnter={onPlayMaskMouseEnter}
                onMouseLeave={onPlayMaskMouseLeave}
              >
                <VideoPlayIcon />
              </StyledPlayMask>
            </Hitarea>
          ) : null}
          <BackgroundVideoProgressBar
            isUploadingOrTranscoding={isUploadingOrTranscoding}
            isUploading={isUploading}
            isTranscoding={isTranscoding}
            uploadProgress={uploadProgress}
            transcodeProgress={transcodeProgress}
          />
        </StyledPreviewVideoWrapper>
      ) : null}
      <BackgroundVideoInfoText
        videoRecord={videoRecord}
        hasVideo={hasVideo}
        isVideoReplaceable={isVideoReplaceable}
      />
      <VStack spacing={2} marginTop="$2">
        <Checkbox
          isChecked={loop ? Boolean(Expressions.getBoolean(loop)) : true}
          onChange={(e) => {
            dispatch(
              updateExpressionAtAction(
                EBoolean(e.target.checked),
                VIDEO_LOOP_ATTR_PATH
              )
            );
          }}
          label="Loop video"
        />
        <Checkbox
          isChecked={
            autoplay ? Boolean(Expressions.getBoolean(autoplay)) : true
          }
          onChange={(e) => {
            dispatch(
              updateExpressionAtAction(
                EBoolean(e.target.checked),
                VIDEO_AUTOPLAY_ATTR_PATH
              )
            );
          }}
          label="Autoplay video"
        />
        <Checkbox
          data-automation-id={fieldWrapperLabel('include-play-pause-button')}
          isChecked={hasPlayPauseButton}
          isDisabled={isOnSecondaryLocale}
          onChange={(e) => {
            dispatch(
              updateExpressionAction(
                e.target.checked
                  ? addDefaultPlayPauseButton(strippedExpr)
                  : removePlayPauseButton(strippedExpr)
              )
            );
          }}
          label="Include play/pause button"
        />
      </VStack>
    </>
  );
};

const removePlayPauseButton = (expr: Expression) => {
  const record = Expressions.getRecord(expr);
  const children = record != null && record.children;
  const childList = children ? Expressions.getList(children) || [] : [];
  return Expressions.setIn(
    expr,
    VIDEO_CHILDREN_PATH,
    EList(
      childList.filter((child) => {
        const childElement = Expressions.getElement(child);
        return childElement != null && !isPlayPauseButton(childElement);
      })
    )
  );
};

const addDefaultPlayPauseButton = (expr: Expression) => {
  const childrenExpression = Expressions.getIn(expr, VIDEO_CHILDREN_PATH);
  const children =
    childrenExpression != null && Expressions.getList(childrenExpression);
  return Expressions.setIn(
    expr,
    VIDEO_CHILDREN_PATH,
    EList([...(children || []), DEFAULT_PLAY_PAUSE_BUTTON])
  );
};

export const BackgroundVideoWrapperType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: BackgroundVideoWrapperTypeRender,
});
